import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { getTimeOffTypes } from '@/modules/settings/api/time-offs.api';
import { TimeOffTypeCode } from '@/modules/settings/types/time-off';

export interface TimeOffTypeFilterOption {
  id: number;
  name: string;
}

/**
 * Loads time-off types for the filter dropdown from the real settings endpoint
 * (`/api/time-off/settings/types`). The returned ids match what the backend
 * expects in the `typeIds` search filter — the previous mock ids silently broke
 * filtering (the payload `typeIds: [1]` referenced a mock id, not a real type).
 *
 * The endpoint returns the full (small) list in one call, so there is no real
 * server pagination; we apply the search/page slice client-side to keep the
 * filter-v2 `loadOptions` contract.
 *
 * Public holidays are excluded — they are system-managed and cannot be requested
 * or filtered on by users.
 */
export const getTimeOffTypesForFilter = async (params: {
  searchString?: string;
  cursor?: number | null;
  pageSize?: number;
}): Promise<PaginatedResponse<TimeOffTypeFilterOption>> => {
  const types = await getTimeOffTypes();

  const search = params.searchString?.toLowerCase() ?? '';
  const filtered = types
    .filter(type => type.code !== TimeOffTypeCode.PUBLIC_HOLIDAY)
    .filter(type => !search || type.displayName.toLowerCase().includes(search))
    .map(type => ({ id: type.id, name: type.displayName }));

  const pageSize = params.pageSize ?? 50;
  const startIndex = params.cursor ?? 0;
  const endIndex = startIndex + pageSize;
  const slice = filtered.slice(startIndex, endIndex);

  return new PaginatedResponse({
    items: slice,
    pagination: {
      total: filtered.length,
      cursor: endIndex < filtered.length ? endIndex : null,
    },
  });
};
